import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Global email-verification popup. Mounted ONCE at the router root.
//
// Opens when:
//  - any API call answers 403 EMAIL_UNVERIFIED (the api.js interceptor
//    dispatches "swiprent:email-unverified"), or
//  - something dispatches the same event manually (signup welcome).
//
// Offers a resend button and refreshes the session after the user clicks
// the emailed link in another tab ("I verified" button).
export default function VerifyEmailGate() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    function onEvent() {
      setOpen(true);
    }
    window.addEventListener("swiprent:email-unverified", onEvent);
    return () => window.removeEventListener("swiprent:email-unverified", onEvent);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Session became verified (link clicked in another tab, refresh polled):
  // close the popup automatically.
  useEffect(() => {
    if (open && user?.email_verified) setOpen(false);
  }, [open, user?.email_verified]);

  const resend = useCallback(async () => {
    setSending(true);
    try {
      const res = await api.post("/auth/resend-verification");
      if (res.data?.already_verified) {
        await refresh();
        setOpen(false);
        return;
      }
      if (res.data?.sent === false) {
        toast.warning(res.data?.message || "Email sending is not set up yet.");
      } else {
        toast.success("Verification email sent. Check your inbox and your spam folder.");
      }
    } catch (error) {
      toast.warning(formatApiError(error));
    } finally {
      setSending(false);
    }
  }, [refresh]);

  const checkAgain = useCallback(async () => {
    setChecking(true);
    try {
      const next = await refresh();
      if (next?.email_verified) {
        toast.success("You are verified. Welcome to SwipRent.");
        setOpen(false);
      } else {
        toast.info("Not verified yet. Click the link in your email first.");
      }
    } finally {
      setChecking(false);
    }
  }, [refresh]);

  // Guests and verified users never see this.
  if (!user || user.email_verified) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Verify your email"
            className="relative w-full max-w-sm rounded-2xl bg-white p-6 pt-8 text-center shadow-ambient"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 rounded-full p-2 text-brand-secondary transition hover:bg-neutral-100 hover:text-brand-ink"
            >
              <X size={18} />
            </button>

            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-tint text-brand-lime">
              <MailCheck size={22} strokeWidth={2} />
            </span>
            <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-brand-ink">
              Verify your email first
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-brand-secondary">
              We sent a verification link to <span className="font-semibold text-brand-ink">{user.email}</span>.
              Swiping, saving, listing and chat unlock the moment you click it. If you do not see
              the email, check your spam or junk folder.
            </p>

            <button
              type="button"
              onClick={checkAgain}
              disabled={checking}
              className="mt-5 w-full rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover disabled:opacity-60"
            >
              {checking ? "Checking" : "I clicked the link"}
            </button>
            <button
              type="button"
              onClick={resend}
              disabled={sending}
              className="mt-2 w-full rounded-full border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-ink transition hover:bg-neutral-50 disabled:opacity-60"
            >
              {sending ? "Sending" : "Resend the email"}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
